/**
 * ROICalculator - Refactored Main Component
 * Orchestrates the ROI calculation flow using sub-components
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ROITypeSelector, B2CROIForm, B2BROIForm, ROIResults, formatCurrency } from './roi';

const ROICalculator = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  
  const [calculatorType, setCalculatorType] = useState(null); // 'individual' or 'corporate'
  
  // Corporate form data
  const [corporateData, setCorporateData] = useState({
    participants: 50,
    avgSalary: 750000,
    turnoverRate: 20,
    annualRevenue: 100000000,
    totalEmployees: 500
  });
  
  // Individual form data
  const [individualData, setIndividualData] = useState({
    currentSalary: 750000,
    targetSalaryIncrease: 15,
    sessionsPerMonth: 4,
    monthsOfCoaching: 6,
    careerGoal: 'promotion'
  });
  
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowResults(false);
      setResults(null);
      setCalculatorType(null);
    }
  }, [isOpen]);

  const validateCorporateForm = () => {
    const newErrors = {};
    
    if (corporateData.participants < 1) {
      newErrors.participants = 'Must be at least 1';
    }
    if (corporateData.participants > corporateData.totalEmployees) {
      newErrors.participants = 'Cannot exceed total employees';
    }
    if (corporateData.avgSalary < 100000) {
      newErrors.avgSalary = 'Must be at least ₺100,000';
    }
    if (corporateData.turnoverRate < 0 || corporateData.turnoverRate > 100) {
      newErrors.turnoverRate = 'Must be between 0-100%';
    }
    if (corporateData.annualRevenue < 1000000) {
      newErrors.annualRevenue = 'Must be at least ₺1,000,000';
    }
    if (corporateData.totalEmployees < 1) {
      newErrors.totalEmployees = 'Must be at least 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateIndividualForm = () => {
    const newErrors = {};
    
    if (individualData.currentSalary < 100000) {
      newErrors.currentSalary = 'Must be at least ₺100,000';
    }
    if (individualData.targetSalaryIncrease < 0 || individualData.targetSalaryIncrease > 100) {
      newErrors.targetSalaryIncrease = 'Must be between 0-100%';
    }
    if (individualData.sessionsPerMonth < 1 || individualData.sessionsPerMonth > 12) {
      newErrors.sessionsPerMonth = 'Must be between 1-12 sessions';
    }
    if (individualData.monthsOfCoaching < 1 || individualData.monthsOfCoaching > 24) {
      newErrors.monthsOfCoaching = 'Must be between 1-24 months';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateCorporateROI = () => {
    if (!validateCorporateForm()) return;
    
    const { participants, avgSalary, turnoverRate, annualRevenue, totalEmployees } = corporateData;
    
    // Cost calculations (in TL)
    const coachingCostPerPerson = 35000;
    const totalCoachingCost = participants * coachingCostPerPerson;
    const opportunityCost = participants * (avgSalary * 0.02);
    const adminCost = participants * 1000;
    const totalCosts = totalCoachingCost + opportunityCost + adminCost;
    
    // Value calculations
    const productivityGain = participants * (avgSalary * 0.15);
    const turnoverReduction = ((participants / totalEmployees) * (turnoverRate / 100) * avgSalary) * 1.5;
    const revenueImpact = (participants / totalEmployees) * 0.02 * annualRevenue;
    const leadershipValue = participants * 15000;
    
    const totalValue = productivityGain + turnoverReduction + revenueImpact + leadershipValue;
    const netValue = totalValue - totalCosts;
    const roiPercentage = ((netValue / totalCosts) * 100);

    setResults({
      totalCosts,
      totalValue,
      netValue,
      roiPercentage,
      breakdown: [
        { label: t('roiCalculator.breakdown.productivityGain', 'Productivity Gain'), value: productivityGain },
        { label: t('roiCalculator.breakdown.turnoverReduction', 'Turnover Cost Reduction'), value: turnoverReduction },
        { label: t('roiCalculator.breakdown.revenueImpact', 'Revenue Impact'), value: revenueImpact },
        { label: t('roiCalculator.breakdown.leadershipValue', 'Leadership Development Value'), value: leadershipValue }
      ],
      insights: `With ${participants} employees in the coaching program, you can expect to see a ${roiPercentage.toFixed(0)}% return on your investment within the first year, primarily driven by productivity improvements and reduced turnover costs.`
    });
    setShowResults(true);
  };

  const calculateIndividualROI = () => {
    if (!validateIndividualForm()) return;
    
    const { currentSalary, targetSalaryIncrease, sessionsPerMonth, monthsOfCoaching, careerGoal } = individualData;
    
    // Coaching cost calculation
    const sessionCost = 1500;
    const totalCoachingCost = sessionsPerMonth * monthsOfCoaching * sessionCost;
    const opportunityCost = (sessionsPerMonth * monthsOfCoaching * 1.5) * (currentSalary / 2080);
    const materialsCost = monthsOfCoaching * 200;
    const totalCosts = totalCoachingCost + opportunityCost + materialsCost;
    
    // Goal-specific multipliers
    const goalMultipliers = {
      promotion: 1.3,
      transition: 1.2,
      leadership: 1.15,
      balance: 0.9
    };
    
    const multiplier = goalMultipliers[careerGoal] || 1.0;
    
    // Value calculations
    const salaryIncrease = currentSalary * (targetSalaryIncrease / 100) * multiplier;
    const careerAcceleration = currentSalary * 0.05;
    const networkValue = currentSalary * 0.03;
    const skillsDevelopment = currentSalary * 0.04;
    const wellbeingValue = careerGoal === 'balance' ? currentSalary * 0.08 : currentSalary * 0.02;
    
    const totalValue = salaryIncrease + careerAcceleration + networkValue + skillsDevelopment + wellbeingValue;
    const netValue = totalValue - totalCosts;
    const roiPercentage = ((netValue / totalCosts) * 100);

    const goalInsights = {
      promotion: `Based on your goal of career advancement, coaching can accelerate your promotion timeline by 6-12 months, resulting in earlier salary increases.`,
      transition: `Career transition coaching typically helps individuals land their target role 40% faster, with better negotiated compensation packages.`,
      leadership: `Leadership development through coaching shows an average 25% improvement in team effectiveness and personal performance ratings.`,
      balance: `Work-life balance coaching leads to 35% reduction in stress levels and 20% increase in overall life satisfaction, impacting long-term career sustainability.`
    };

    setResults({
      totalCosts,
      totalValue,
      netValue,
      roiPercentage,
      breakdown: [
        { label: t('roiCalculator.breakdown.salaryIncrease', 'Projected Salary Increase'), value: salaryIncrease },
        { label: t('roiCalculator.breakdown.careerAcceleration', 'Career Acceleration Value'), value: careerAcceleration },
        { label: t('roiCalculator.breakdown.networkValue', 'Professional Network Value'), value: networkValue },
        { label: t('roiCalculator.breakdown.skillsDevelopment', 'Skills Development Value'), value: skillsDevelopment },
        { label: t('roiCalculator.breakdown.wellbeingValue', 'Wellbeing & Balance Value'), value: wellbeingValue }
      ],
      insights: goalInsights[careerGoal]
    });
    setShowResults(true);
  };

  const handleCorporateInputChange = (field, value) => {
    const numValue = parseInt(value.replace(/,/g, ''), 10) || 0;
    setCorporateData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleIndividualInputChange = (field, value) => {
    if (field === 'careerGoal') {
      setIndividualData(prev => ({ ...prev, [field]: value }));
    } else {
      const numValue = parseInt(value.replace(/,/g, ''), 10) || 0;
      setIndividualData(prev => ({ ...prev, [field]: numValue }));
    }
  };

  const handleReset = () => {
    setShowResults(false);
    setResults(null);
  };

  if (!isOpen) return null;

  // Show results
  if (showResults && results) {
    return (
      <ROIResults
        results={results}
        calculatorType={calculatorType}
        onClose={onClose}
        onReset={handleReset}
      />
    );
  }

  // Type Selection Screen
  if (!calculatorType) {
    return (
      <ROITypeSelector
        onClose={onClose}
        onSelectType={setCalculatorType}
      />
    );
  }

  // Individual Calculator Form
  if (calculatorType === 'individual') {
    return (
      <B2CROIForm
        data={individualData}
        errors={errors}
        onChange={handleIndividualInputChange}
        onCalculate={calculateIndividualROI}
        onBack={() => setCalculatorType(null)}
        onClose={onClose}
      />
    );
  }

  // Corporate Calculator Form
  if (calculatorType === 'corporate') {
    return (
      <B2BROIForm
        data={corporateData}
        errors={errors}
        onChange={handleCorporateInputChange}
        onCalculate={calculateCorporateROI}
        onBack={() => setCalculatorType(null)}
        onClose={onClose}
      />
    );
  }

  return null;
};

export default ROICalculator;
